import { markAsSyntheticFallback } from "@amzn/sdo-privacy-console-sdk/dist/synthetic-fallbacks";
import { createFallbackComponent } from "./create-fallback-component";

/**
 * Produces a synthetic “fallback module” for React.lazy.
 *
 * Why:
 * - Callers are expected to render remotes via our SDK components.
 * - If a remote load fails, we still want the SDK to drive the final,
 *   contextual fallback UI/telemetry. To enable that, we return a
 *   recognizable synthetic module here, which the SDK will intercept
 *   and upgrade to its richer fallback.
 * - If someone bypasses the SDK and renders a remote directly via MF,
 *   they will see the generic fallback component exported by this module.
 *
 * @returns An ES-module-shaped object `{ __esModule: true, default: Component }`
 *          that React.lazy can consume.
 */
export const createSyntheticModuleFallback = async ({ id }: { id: string }) => {
    const FallbackComponent = await createFallbackComponent({ id });

    // Create the fallback module
    const fallbackModule = {
        __esModule: true,
        default: FallbackComponent,
    };

    // Mark as synthetic so SDK loaders can detect that this is an injected fallback module
    // rather than the successfully loaded module that was requested.
    markAsSyntheticFallback(fallbackModule);

    return fallbackModule;
};
